/**
 * Utility functions to parse and transform form definition from API
 */

/**
 * Get label from localized label object
 * @param {Object} labelObj - Label object with language keys
 * @param {string} languageId - Language ID (default: 'en_US')
 * @returns {string} Label text
 */
const getLabel = (labelObj, languageId = 'en_US') => {
  if (!labelObj) return '';
  if (typeof labelObj === 'string') return labelObj;
  return labelObj[languageId] || labelObj[Object.keys(labelObj)[0]] || '';
};

/**
 * Parse regex parameter from validation configuration
 * @param {*} regexParam - Regex parameter value
 * @returns {string|undefined} Parsed regex pattern
 */
const parseRegexParameter = (regexParam) => {
  if (!regexParam) return undefined;
  
  try {
    const param = typeof regexParam === 'string' ? JSON.parse(regexParam) : regexParam;
    return typeof param === 'string' ? param : param.value || param;
  } catch {
    return regexParam;
  }
};

const parseValidation = (field) => {
  const requiredMsg = getLabel(field.requiredErrorMessage);
  const validation = {
    required: field.required || false,
    requiredErrorMessage: requiredMsg,
    errorMessage: requiredMsg,
    message: requiredMsg,
  };

  // Extract expression parsing to reduce complexity
  const expr = field.validation?.expression;
  const exprName = expr?.name;
  
  if (exprName === 'regularExpression') {
    parseRegexValidation(validation, field);
  } else if (exprName === 'eq') {
    parseExactLengthValidation(validation, field);
  }

  // Handle date validation separately
  if (field.type === 'date' && field.validation?.parameter) {
    validation.dateValidation = field.validation.parameter;
  }

  return validation;
};

// Helper function to parse regex validation
const parseRegexValidation = (validation, field) => {
  const param = field.validation.expression?.parameter || field.validation.parameter;
  validation.regex = parseRegexParameter(param?.en_US || param);
  const msg = getLabel(field.validation.errorMessage);
  if (msg) {
    validation.errorMessage = msg;
    validation.message = msg;
  }
};

// Helper function to parse exact length validation
const parseExactLengthValidation = (validation, field) => {
  const param = field.validation.expression?.parameter || field.validation.parameter;
  const exactLength = parseInt(param?.en_US || param, 10);
  if (!isNaN(exactLength)) {
    validation.exactLength = exactLength;
    const isMobileField = field.type === 'numeric' || field.fieldReference?.toLowerCase().includes('mobile');
    if (isMobileField) {
      validation.maxLength = exactLength;
    }
    const msg = getLabel(field.validation.errorMessage);
    if (msg) {
      validation.errorMessage = msg;
      validation.message = msg;
    }
  }
};

/**
 * Parse options for select/radio fields
 * @param {Array} options - Options array from API
 * @returns {Array} Parsed options
 */
const parseOptions = (options = []) => {
  return options.map(option => ({
    value: option.value || option.reference,
    label: getLabel(option.label),
    reference: option.reference,
  }));
};

const getDefaultValue = (predefinedValue) => {
  if (!predefinedValue) return '';
  const value = typeof predefinedValue === 'string' ? predefinedValue : predefinedValue.en_US;
  if (!value || value === 'null' || value === '[]') return '';
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? '' : parsed;
  } catch {
    return value;
  }
};

const transformField = (apiField) => {
  const field = {
    name: apiField.name,
    fieldName: apiField.name,
    fieldReference: apiField.fieldReference,
    type: apiField.type,
    label: getLabel(apiField.label),
    placeholder: getLabel(apiField.placeholder),
    required: apiField.required || false,
    readOnly: apiField.readOnly || false,
    validation: parseValidation(apiField),
    defaultValue: getDefaultValue(apiField.predefinedValue),
  };

  if (['select', 'radio'].includes(apiField.type)) {
    field.options = parseOptions(apiField.options);
  } else if (apiField.type === 'numeric') {
    field.type = 'number';
    field.dataType = apiField.dataType || 'integer';
  } else if (apiField.type === 'text') {
    const regex = apiField.validation?.expression?.parameter?.en_US;
    if (regex?.includes('@') && regex.includes('email')) field.type = 'email';
  }

  const tooltip = apiField.tip || apiField.tooltip;
  if (tooltip) field.tooltip = getLabel(tooltip);

  return field;
};

export const transformFormDefinition = (apiResponse) => {
  if (!apiResponse?.fields) return { fieldGroups: [] };
  
  const fieldGroups = apiResponse.fields
    .filter(f => f.type === 'fieldset')
    .map(fieldset => {
      const label = getLabel(fieldset.label);
      return {
        label,
        title: label,
        name: fieldset.fieldReference || fieldset.name,
        fields: (fieldset.nestedFields || []).map(transformField),
      };
    });

  return { ...apiResponse, fieldGroups };
};
